<template>
	<div class="atc" ref="root">
		<div class="atc-head">
			<span class="atc-title">{{ title }}</span>
			<span v-if="series.length > 1" class="atc-legend">
				<span v-for="s in series" :key="s.key" class="atc-legend-item">
					<span class="atc-key" :style="{ background: s.color }"></span>{{ s.label }}
				</span>
			</span>
		</div>

		<div v-if="points.length < 2" class="atc-empty">Not enough reports for a trend yet.</div>

		<div v-else class="atc-plot" @mouseleave="hover = null">
			<svg :width="width" :height="height" role="img" :aria-label="ariaLabel">
				<!-- recessive grid + y ticks -->
				<g v-for="t in ticks" :key="'t' + t">
					<line :x1="pad.l" :x2="width - pad.r" :y1="y(t)" :y2="y(t)" class="atc-grid" />
					<text :x="pad.l - 6" :y="y(t) + 3" text-anchor="end" class="atc-axis">{{ format(t) }}</text>
				</g>
				<text :x="pad.l" :y="height - 4" class="atc-axis">{{ points[0].label }}</text>
				<text :x="width - pad.r" :y="height - 4" text-anchor="end" class="atc-axis">{{ points[points.length - 1].label }}</text>

				<path v-for="s in series" :key="'l' + s.key" :d="path(s.key)" fill="none" :stroke="s.color" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />

				<!-- direct label on the latest value -->
				<g v-for="s in series" :key="'e' + s.key">
					<circle :cx="x(points.length - 1)" :cy="y(val(points.length - 1, s.key))" r="4" :fill="s.color" stroke="#fff" stroke-width="2" />
					<text :x="x(points.length - 1) + 8" :y="y(val(points.length - 1, s.key)) + 3" class="atc-value">{{ format(val(points.length - 1, s.key)) }}</text>
				</g>

				<g v-if="hover !== null">
					<line :x1="x(hover)" :x2="x(hover)" :y1="pad.t" :y2="height - pad.b" class="atc-cross" />
					<circle
						v-for="s in series"
						:key="'h' + s.key"
						:cx="x(hover)"
						:cy="y(val(hover, s.key))"
						r="4"
						:fill="s.color"
						stroke="#fff"
						stroke-width="2"
					/>
				</g>

				<rect :x="pad.l" :y="0" :width="Math.max(0, width - pad.l - pad.r)" :height="height" fill="transparent" @mousemove="onMove" />
			</svg>

			<div v-if="hover !== null" class="atc-tip" :style="tipStyle">
				<div class="atc-tip-label">{{ points[hover].tooltip || points[hover].label }}</div>
				<div v-for="s in series" :key="'tip' + s.key" class="atc-tip-row">
					<span class="atc-tip-key" :style="{ background: s.color }"></span>
					<strong>{{ format(val(hover, s.key)) }}</strong>
					<span class="atc-tip-name">{{ s.label }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// Small SVG line chart for report trends. One y-axis only — measures on a
// different scale go into a second chart. Points are oldest → newest.
export default {
	name: 'AdaTrendChart',
	props: {
		title: { type: String, default: '' },
		points: { type: Array, required: true }, // [{ label, tooltip?, values: { key: number } }]
		series: { type: Array, required: true }, // [{ key, label, color }]
		yMax: { type: Number, default: null },
		suffix: { type: String, default: '' },
		height: { type: Number, default: 150 },
	},
	data() {
		return { width: 400, hover: null, pad: { l: 40, r: 44, t: 10, b: 20 }, observer: null }
	},
	computed: {
		max() {
			if (this.yMax !== null) return this.yMax
			let m = 0
			for (const p of this.points) for (const s of this.series) m = Math.max(m, Number(p.values[s.key]) || 0)
			return this.niceMax(m)
		},
		ticks() {
			return [0, this.max / 2, this.max]
		},
		ariaLabel() {
			const last = this.points[this.points.length - 1]
			if (!last) return this.title
			return `${this.title}: latest ` + this.series.map((s) => `${s.label} ${this.format(last.values[s.key])}`).join(', ')
		},
		tipStyle() {
			const left = this.x(this.hover)
			const flip = left > this.width - 160
			return flip ? { right: this.width - left + 10 + 'px', top: '8px' } : { left: left + 10 + 'px', top: '8px' }
		},
	},
	mounted() {
		this.measure()
		if (window.ResizeObserver) {
			this.observer = new ResizeObserver(() => this.measure())
			this.observer.observe(this.$refs.root)
		}
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect()
	},
	methods: {
		measure() {
			if (this.$refs.root) this.width = Math.max(200, this.$refs.root.clientWidth)
		},
		niceMax(m) {
			if (m <= 0) return 10
			const pow = Math.pow(10, Math.floor(Math.log10(m)))
			for (const step of [1, 2, 2.5, 5, 10]) if (step * pow >= m) return step * pow
			return 10 * pow
		},
		val(i, key) {
			return Number(this.points[i].values[key]) || 0
		},
		x(i) {
			const n = this.points.length - 1
			return this.pad.l + (n ? (i / n) * (this.width - this.pad.l - this.pad.r) : 0)
		},
		y(v) {
			return this.pad.t + (1 - v / this.max) * (this.height - this.pad.t - this.pad.b)
		},
		path(key) {
			return this.points.map((_, i) => `${i ? 'L' : 'M'}${this.x(i).toFixed(1)},${this.y(this.val(i, key)).toFixed(1)}`).join(' ')
		},
		format(v) {
			const n = Math.round(Number(v) || 0)
			return n.toLocaleString() + this.suffix
		},
		onMove(e) {
			const box = e.currentTarget.getBoundingClientRect()
			const rel = (e.clientX - box.left) / Math.max(1, box.width)
			this.hover = Math.max(0, Math.min(this.points.length - 1, Math.round(rel * (this.points.length - 1))))
		},
	},
}
</script>

<style scoped>
.atc {
	position: relative;
	min-width: 0;
}

.atc-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 6px;
}

.atc-title {
	font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Monaco, Consolas, monospace;
	font-size: 11px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.atc-legend {
	display: flex;
	gap: 12px;
	font-size: 12px;
	color: #374151;
}

.atc-legend-item {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.atc-key,
.atc-tip-key {
	display: inline-block;
	width: 14px;
	height: 2px;
	border-radius: 1px;
}

.atc-plot {
	position: relative;
}

.atc-plot svg {
	display: block;
	overflow: visible;
}

.atc-grid {
	stroke: #f1f3f5;
	stroke-width: 1;
}

.atc-axis {
	font-size: 10px;
	fill: #9ca3af;
}

.atc-value {
	font-size: 11px;
	font-weight: 600;
	fill: #111827;
}

.atc-cross {
	stroke: #9ca3af;
	stroke-width: 1;
}

.atc-tip {
	position: absolute;
	pointer-events: none;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 6px 10px;
	box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
	font-size: 12px;
	color: #111827;
	white-space: nowrap;
	z-index: 2;
}

.atc-tip-label {
	color: #6b7280;
	margin-bottom: 2px;
}

.atc-tip-row {
	display: flex;
	align-items: center;
	gap: 6px;
}

.atc-tip-name {
	color: #6b7280;
}

.atc-empty {
	font-size: 13px;
	color: #6b7280;
	padding: 24px 0;
}
</style>
